import React, { useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useCfoSOCCoverage } from '../../../hooks/analytics/useCfoReports.ts';
import { RefreshMeta } from '../controller/shared.tsx';
import type { CFOSOCCoverageRow } from '../../../request/analytics/cfoReports.ts';

const CheckCell: React.FC<{ value: boolean | null | undefined }> = ({ value }) =>
  value ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-gray-300" />;

/** Report #5 — SOC Report Coverage. Table with check cells per SOC type + a year filter. */
export const SOCCoverageTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoSOCCoverage();
  const rows = data ?? [];
  const [year, setYear] = useState<string>('all');

  const years = useMemo(
    () => Array.from(new Set(rows.map((r) => r.year).filter((y): y is number => y != null))).sort((a, b) => b - a),
    [rows]
  );

  const filtered = useMemo(
    () => (year === 'all' ? rows : rows.filter((r) => String(r.year) === year)),
    [rows, year]
  );

  const columns = useMemo<TableColumnDef<CFOSOCCoverageRow>[]>(
    () => [
      {
        id: 'name',
        columnLabel: 'System',
        header: () => 'System',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {row.original.name}
            {row.original.key_system && (
              <span className="ml-2 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                Key
              </span>
            )}
          </span>
        ),
        widthPercent: 26,
      },
      { id: 'year', columnLabel: 'Year', header: () => 'Year', cell: ({ row }) => row.original.year ?? '—', widthPercent: 10 },
      {
        id: 'soc1_type1',
        columnLabel: 'SOC1 Type1',
        header: () => 'SOC1 T1',
        cell: ({ row }) => <CheckCell value={row.original.soc1_type1} />,
        widthPercent: 12,
      },
      {
        id: 'soc1_type2',
        columnLabel: 'SOC1 Type2',
        header: () => 'SOC1 T2',
        cell: ({ row }) => <CheckCell value={row.original.soc1_type2} />,
        widthPercent: 12,
      },
      {
        id: 'soc2_type1',
        columnLabel: 'SOC2 Type1',
        header: () => 'SOC2 T1',
        cell: ({ row }) => <CheckCell value={row.original.soc2_type1} />,
        widthPercent: 12,
      },
      {
        id: 'soc2_type2',
        columnLabel: 'SOC2 Type2',
        header: () => 'SOC2 T2',
        cell: ({ row }) => <CheckCell value={row.original.soc2_type2} />,
        widthPercent: 12,
      },
      {
        id: 'bridge_letter',
        columnLabel: 'Bridge Letter',
        header: () => 'Bridge',
        cell: ({ row }) => <CheckCell value={row.original.bridge_letter} />,
        widthPercent: 12,
      },
    ],
    []
  );

  return (
    <AppDataTable<CFOSOCCoverageRow>
      data={filtered}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load SOC report coverage.' : null}
      emptyMessage="No systems with SOC coverage tracked."
      searchable
      searchPlaceholder="Search systems..."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full items-center justify-between gap-3">
          {years.length > 0 ? (
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1"
            >
              <option value="all">All years</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          ) : (
            <span />
          )}
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default SOCCoverageTable;
